import type { TmCountryCode } from '@/services/types'
import { TmFakeCountryCode } from '@/services/types'
import { countryPhoneData } from '@/constants/countryPhoneData'
import { addPlusToPhoneNumber } from '@/utils/string/addPlusToPhoneNumber'
import { getRandomDigit } from '@/utils/number'

export default class CountryPhoneService {
  private topCountries: TmCountryCode[] = ['US', 'GB', 'AU', 'CA']

  public getCountryPhones(onlyRealCountries = false) {
    const filteredCountries = this.filterCountryOptions(countryPhoneData, onlyRealCountries)
    return this.sortCountryOptions(filteredCountries)
  }

  public getCountryCodes(onlyRealCountries = false) {
    return this.getCountryPhones(onlyRealCountries).map(({ value }) => value)
  }

  public getRandomizedPhoneNumber(countryCode: TmCountryCode) {
    const option = this.getCountryPhone(countryCode)
    if (!option) return ''

    const prefix = this.getPhonePrefix(countryCode)

    const mask = Array.isArray(option.mask) ? option.mask[0] : option.mask
    const number = mask.replace(/#/g, () => String(getRandomDigit()))
    return `${prefix} ${number}`
  }

  public getPhonePrefix(countryCode: TmCountryCode) {
    const option = this.getCountryPhone(countryCode)
    if (!option) return ''

    return addPlusToPhoneNumber(option.code)
  }

  public getCountryPhone(countryCode: TmCountryCode) {
    return countryPhoneData.find(({ value }) => value === countryCode)
  }

  public filterCountryOptions<T extends { value: TmCountryCode }>(countries: T[], onlyRealCountries: boolean): T[] {
    if (!onlyRealCountries) return countries
    return countries.filter(({ value }) => value !== TmFakeCountryCode)
  }

  public sortCountryOptions<T extends { value: TmCountryCode }>(countries: T[]): T[] {
    const orderList = this.topCountries.reverse()
    return countries.sort((a, b) => {
      const aOrder = orderList.indexOf(a.value)
      const bOrder = orderList.indexOf(b.value)
      return bOrder - aOrder
    })
  }

  public makeInternationalPhone(countryCode: TmCountryCode, phoneNumber: string) {
    if (phoneNumber.startsWith('+')) return phoneNumber
    if (phoneNumber.startsWith('00')) return `+${phoneNumber.slice(2)}`

    const prefix = this.getPhonePrefix(countryCode)
    return `${prefix}${phoneNumber}`
  }
}

export const countryPhoneService = new CountryPhoneService()
